import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App'
import { useAuthStore } from './store/authStore'
import { analytics } from './lib/analytics'

// Regex patterns for PII we must never send to Sentry
const PII_PATTERNS = [
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,  // email addresses
  /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,                    // IPv4 addresses
  /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, // JWT tokens
]

function _scrubPii(value: unknown): unknown {
  if (typeof value === 'string') {
    return PII_PATTERNS.reduce((s, re) => s.replace(re, '[REDACTED]'), value)
  }
  if (Array.isArray(value)) return value.map(_scrubPii)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, _scrubPii(v)])
    )
  }
  return value
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD,
  tracesSampleRate: 0.1,
  // Strip all PII before the event leaves the browser
  beforeSend(event) {
    // Remove user context entirely — we don't need it in error reports
    delete event.user
    // Scrub message, exception values, breadcrumbs, and extra context
    return _scrubPii(event) as typeof event
  },
})

// Service worker disabled — kill switch deployed to clear stale SW cache
// Re-enable once SW cache issues are fully resolved

// Init analytics
analytics.init()

// Init Supabase auth listener
useAuthStore.getState().init()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
